/**
 * PdfExport.js
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

// PDF Export utility using jsPDF
import { debugPdfExport } from './debug.js'
import { APP_VERSION } from './SavedPlans.js'
import { Filesystem, Directory } from '@capacitor/filesystem'
import { Share } from '@capacitor/share'
import { analyzeAlerts } from './alerts.js'
// The environment formatting lives next to the HTML export so both documents
// name the water and the altitude the same way, from a single implementation.
import { HtmlExport, exportEnvironmentRows } from './HtmlExport.js'
// Document metadata (Info dictionary and XMP packet), written from a single
// place so a sheet, a day of diving and a shared file all name the same tool.
import { createPdfDocument, applyPdfMetadata, divePdfMetadata } from './PdfMetadata.js'
import {
  shortGasName,
  gasLabels,
  gasConsumptionRows,
  filterDisplaySegments,
  getMaxDepth,
  getBottomTime,
  calculateMOD,
  getProfileData,
  getStressUntilText,
  getSegmentTypeLabel,
  surfaceIntervalSentences,
  noFlyValue,
  exposureShown,
  formatDensity,
  segmentDensity,
  densityLevel
} from './planUtils.js'

function isNativePlatform() {
  return typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()
}

export class PdfExport {
  constructor(plan, settings, ccr, translateFn, bailoutGases = [], environment = {}) {
    this.plan = plan
    this.settings = settings
    this.ccr = ccr
    this.t = translateFn || ((key) => key)
    this.bailoutGases = bailoutGases
    this.environment = environment
    // Built once per document: the labels the tables print for each gas name.
    this.gasLabelCache = null
    debugPdfExport('PdfExport created: mode=%s, bailoutGases=%d', ccr.enabled ? 'CCR' : 'OC', bailoutGases.length)
  }

  // Name to print for a gas in the document: the whole name, never a short
  // form.
  //
  // The sheet is what leaves the screen - printed, filed, carried to the water -
  // and a cylinder named 'TX' on paper cannot be looked up again. The tables
  // that print gases here are wide enough for a full name (26 mm in the CCR
  // segment table, 30 mm in open circuit, 35 mm in the requirement table), so
  // nothing is gained by abbreviating. The only exception is the bailout grid
  // of the condensed sheet, whose cells are a quarter of a diver column: it
  // goes through gasCodeOf.
  gasLabelOf(name) {
    return name || ''
  }

  // Short code for the narrow cells of the condensed sheet, shortened only
  // where the short form still names one cylinder and one only.
  //
  // shortGasName alone turned 'TX 17/37' into 'TX' while a 'TX14/48' sat in the
  // same list, so the grid named neither. The whole set of names the document
  // may show is resolved together, cylinders and breathed mixes alike, so a gas
  // that has to be abbreviated is abbreviated the same way throughout.
  gasCodeOf(name) {
    if (!name) return ''
    if (!this.gasLabelCache) {
      const names = new Set()
      for (const g of (this.bailoutGases || [])) if (g?.name) names.add(g.name)
      for (const plan of [this.plan?.ccr, this.plan?.bailout]) {
        for (const seg of (plan?.segments || [])) {
          if (seg.gas) names.add(seg.gas)
          if (seg.gasName) names.add(seg.gasName)
        }
        for (const c of (plan?.gasConsumption || [])) if (c.name) names.add(c.name)
      }
      const ordered = [...names]
      const labels = gasLabels(ordered)
      this.gasLabelCache = new Map(ordered.map((n, i) => [n, labels[i]]))
    }
    return this.gasLabelCache.get(name) ?? shortGasName(name)
  }

  // [label, value] pairs naming the environment the plan was computed for.
  // Shared with the HTML export so both documents say the same thing.
  environmentRows() {
    return exportEnvironmentRows(this.environment, (key, params) => this.t(key, params))
  }

  // Name MV-Plan and describe the dive in the document properties. The file is
  // what leaves the application - shared, mailed, archived - and a sheet whose
  // properties only say "jsPDF" cannot be traced back to the planner that
  // computed it, nor searched for by depth, mix or dive site.
  stampMetadata(doc) {
    return applyPdfMetadata(doc, divePdfMetadata({
      plan: this.plan,
      settings: this.settings,
      ccr: this.ccr,
      environment: this.environment
    }))
  }

  // Build a full A4 PDF document with jsPDF: header, dive site/divers,
  // summary box (depth/time/runtime/TTS/CNS/OTU), config grid, bailout gases,
  // CCR and/or OC plan tables with profiles, gas consumption, notes, and footer.
  generatePdf() {
    const { ccr: ccrPlan, bailout } = this.plan
    const isCCR = this.ccr.enabled
    const date = new Date().toLocaleDateString()
    const time = new Date().toLocaleTimeString()

    const doc = createPdfDocument()
    this.stampMetadata(doc)

    const pageWidth = doc.internal.pageSize.getWidth()
    const margin = 15
    let y = 20

    // Header
    doc.setFontSize(18)
    doc.setFont('helvetica', 'bold')
    doc.text(this.t('pdf_title'), margin, y)

    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(100)
    doc.text(`${this.t('pdf_generated')}: ${date} ${time}`, pageWidth - margin, y, { align: 'right' })
    doc.setTextColor(0)

    y += 8

    // Dive site (if configured)
    const diveSite = this.settings.diveSite?.trim()
    if (diveSite) {
      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      const siteLabel = `${this.t('settings_dive_site')}: `
      doc.text(siteLabel, margin, y)
      const siteLabelWidth = doc.getTextWidth(siteLabel)
      doc.setFont('helvetica', 'normal')
      doc.text(diveSite, margin + siteLabelWidth, y)
      y += 7
    }

    // Diver names (if configured)
    const diverNames = this.settings.diverNames?.trim()
    if (diverNames) {
      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      const diversLabel = `${this.t('settings_divers')}: `
      doc.text(diversLabel, margin, y)
      const diversLabelWidth = doc.getTextWidth(diversLabel)
      doc.setFont('helvetica', 'normal')
      doc.text(diverNames, margin + diversLabelWidth, y)
      y += 7
    }

    // Dive summary box (Max depth, Bottom time, Runtime, TTS, CNS, OTU)
    const summaryPlan = isCCR ? ccrPlan : bailout
    if (summaryPlan) {
      y += 3
      const maxDepth = getMaxDepth(summaryPlan)
      const bottomTime = getBottomTime(summaryPlan)
      const runtime = Math.ceil(summaryPlan.runtime || 0)
      const tts = summaryPlan.tts || 0
      // Series figures on a repetitive dive, through the single helper
      const exposure = exposureShown(summaryPlan)

      const noFly = noFlyValue(summaryPlan, this.t.bind(this))

      const summaryItems = [
        { value: `${maxDepth}m`, label: this.t('plan_max_depth'), warn: false },
        { value: `${bottomTime}'`, label: this.t('plan_bottom_time'), warn: false },
        { value: `${runtime}'`, label: this.t('plan_runtime'), warn: false },
        { value: `${tts}'`, label: this.t('plan_tts'), warn: false },
        { value: `${exposure.cns}%`, label: this.t(exposure.series ? 'plan_cns_series' : 'plan_cns'), warn: exposure.cnsWarning },
        { value: String(exposure.otu), label: this.t(exposure.series ? 'plan_otu_series' : 'plan_otu'), warn: exposure.otuWarning }
      ]
      if (noFly) {
        summaryItems.push({ value: noFly, label: this.t('plan_no_fly'), warn: false })
      }

      // Boxes share the usable width, 5 mm of gutter between each of them
      const gutter = 5
      const summaryBoxWidth = (pageWidth - 2 * margin - gutter * (summaryItems.length - 1)) / summaryItems.length
      const boxHeight = 16

      summaryItems.forEach((item, i) => {
        const bx = margin + i * (summaryBoxWidth + gutter)
        doc.setFillColor(230, 245, 255)
        doc.roundedRect(bx, y, summaryBoxWidth, boxHeight, 2, 2, 'F')

        doc.setFontSize(12)
        doc.setFont('helvetica', 'bold')
        if (item.warn) {
          doc.setTextColor(133, 100, 4)
        } else {
          doc.setTextColor(0, 80, 150)
        }
        doc.text(item.value, bx + summaryBoxWidth / 2, y + 7, { align: 'center' })

        doc.setFontSize(6)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(80)
        doc.text(item.label, bx + summaryBoxWidth / 2, y + 13, { align: 'center' })
      })

      doc.setTextColor(0)
      y += 20

      // Surface interval, under the summary boxes: computed desaturation, then
      // the DAN rule the no-fly box comes from
      const { desat, danCase, danRule, danSource } = surfaceIntervalSentences(summaryPlan, this.t.bind(this))
      const surfaceInterval = [desat, danCase, danRule, danSource].filter(Boolean).join(' ')
      if (surfaceInterval) {
        doc.setFontSize(8)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(80)
        const lines = doc.splitTextToSize(surfaceInterval, pageWidth - 2 * margin)
        doc.text(lines, margin, y)
        doc.setTextColor(0)
        y += lines.length * 4 + 1
      }
    }

    y += 5

    // Configuration section
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.text(this.t('plan_config'), margin, y)
    y += 2
    doc.setDrawColor(200)
    doc.line(margin, y, pageWidth - margin, y)
    y += 8

    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')

    const configItems = [
      [this.t('settings_algorithm'), this.settings.algorithm.toUpperCase()],
      [this.t('settings_gf'), `${Math.round(this.settings.gfLow * 100)}/${Math.round(this.settings.gfHigh * 100)}`],
      [this.t('plan_mode'), isCCR ? 'CCR' : this.t('pdf_mode_oc')],
      [this.t('settings_descent'), `${this.settings.descentRate} m/min`],
      [this.t('settings_ascent'), `${this.settings.ascentRate} m/min`],
      [this.t('settings_deco_ascent'), `${this.settings.decoAscentRate} m/min`],
      [this.t('settings_last_stop_ascent'), `${this.settings.lastStopAscentRate} m/min`],
      [this.t('settings_last_stop'), `${this.settings.lastStopDepth} m`]
    ]

    if (isCCR) {
      configItems.push([this.t('settings_setpoint_mode'), this.ccr.mode === 'fixed' ? this.t('settings_setpoint_fixed') : this.t('settings_setpoint_dynamic')])
      configItems.push(['Setpoint', this.ccr.mode === 'fixed' ? String(this.ccr.fixedSetpoint) : `${this.ccr.dynamicSetpoints.descent}/${this.ccr.dynamicSetpoints.bottom}/${this.ccr.dynamicSetpoints.ascent}`])
      configItems.push([this.t('plan_diluent'), this.ccr.diluent?.name || 'Air'])
    }

    // Environment the plan was computed for, last cells of the grid: the water,
    // and the altitude when the dive is not at sea level. Same reason the
    // gradient factors and the ascent rates are printed here.
    this.environmentRows().forEach(row => configItems.push(row))

    // Draw config as 3-column grid
    const colWidth = (pageWidth - 2 * margin) / 3
    configItems.forEach((item, i) => {
      const col = i % 3
      const row = Math.floor(i / 3)
      const x = margin + col * colWidth
      const itemY = y + row * 12

      doc.setFillColor(245, 245, 245)
      doc.roundedRect(x, itemY - 4, colWidth - 3, 10, 1, 1, 'F')

      doc.setFontSize(7)
      doc.setTextColor(100)
      doc.text(item[0], x + 2, itemY)
      doc.setFontSize(9)
      doc.setTextColor(0)
      doc.setFont('helvetica', 'bold')
      doc.text(item[1], x + 2, itemY + 5)
      doc.setFont('helvetica', 'normal')
    })

    y += Math.ceil(configItems.length / 3) * 12 + 10

    // Bailout gases with MOD (for CCR mode)
    if (isCCR && this.bailoutGases && this.bailoutGases.length > 0) {
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      const bailoutLabel = `${this.t('gases_bailout')}: `
      doc.text(bailoutLabel, margin, y)
      const bailoutLabelWidth = doc.getTextWidth(bailoutLabel)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(9)

      const bailoutText = this.bailoutGases
        .map(g => `${g.name} (MOD ${calculateMOD(g, this.settings.ppO2Max)}m)`)
        .join('  |  ')
      doc.text(bailoutText, margin + bailoutLabelWidth, y)
      y += 8
    }

    const includeProfile = this.settings.pdfIncludeProfile !== false

    // CCR Plan section with profile
    if (isCCR && ccrPlan) {
      y = this.renderPlanSectionPdf(doc, this.t('plan_ccr'), ccrPlan, true, margin, y, pageWidth)
      // CCR profile directly under CCR table
      if (includeProfile) {
        y = this.renderProfilePdf(doc, ccrPlan, margin, y, pageWidth)
      }
    }

    // Bailout / OC Plan section with profile
    if (bailout) {
      const title = isCCR ? this.t('plan_bailout') : this.t('plan_deco')
      y = this.renderPlanSectionPdf(doc, title, bailout, false, margin, y, pageWidth)
      // Bailout/OC profile directly under bailout table
      // For bailout, combine CCR + bailout for full profile visualization
      if (includeProfile) {
        y = this.renderProfilePdf(doc, bailout, margin, y, pageWidth, isCCR ? ccrPlan : null)
      }
    }

    // Gas consumption section
    if (bailout?.gasConsumption) {
      const gasTitle = isCCR ? this.t('plan_gas_requirements') : this.t('plan_gas_consumption')
      y = this.renderGasConsumptionPdf(doc, bailout.gasConsumption, gasTitle, margin, y, pageWidth, isCCR)
    }

    // Dive notes section
    const diveNotes = this.settings.diveNotes?.trim()
    if (diveNotes) {
      y = this.renderNotesPdf(doc, diveNotes, margin, y, pageWidth)
    }

    // Footer
    y = doc.internal.pageSize.getHeight() - 24
    doc.setFontSize(8)
    doc.setTextColor(150)
    doc.text(`${this.t('pdf_footer_generated')} v${APP_VERSION}`, pageWidth / 2, y, { align: 'center' })
    doc.text(this.t('pdf_footer_verify'), pageWidth / 2, y + 4, { align: 'center' })
    doc.setFontSize(7)
    doc.text(this.t('disclaimer_short'), pageWidth / 2, y + 8, { align: 'center' })

    return doc
  }

  // Draw a bordered cell with optional fill and centered/aligned text.
  // opts: { align: 'left'|'center'|'right', valign: 'middle'|'top', bold, size,
  //         fill: [r,g,b], color: [r,g,b] }
  // Write one table cell, shrinking the font for this cell alone when the text
  // is wider than its column.
  //
  // Nothing clips a jsPDF text run: a gas named in full in a 26 mm column would
  // simply be drawn over the setpoint beside it, and the sheet would show two
  // overlapping numbers. Shrinking keeps the whole name, which is the point of
  // printing it in full; the floor is 5 pt, below which the line stops being
  // readable and a slight overflow is the lesser evil.
  drawFittedText(doc, text, x, y, maxWidth) {
    const size = doc.getFontSize()
    let fitted = size
    while (fitted > 5 && doc.getTextWidth(text) > maxWidth) {
      fitted -= 0.5
      doc.setFontSize(fitted)
    }
    doc.text(text, x, y)
    doc.setFontSize(size)
  }

  drawCell(doc, x, y, w, h, text, opts = {}) {
    const {
      align = 'left',
      valign = 'middle',
      bold = false,
      size = 9,
      fill = null,
      color = [0, 0, 0]
    } = opts

    if (fill) {
      doc.setFillColor(...fill)
      doc.rect(x, y, w, h, 'F')
    }
    doc.setDrawColor(0)
    doc.setLineWidth(0.2)
    doc.rect(x, y, w, h)

    if (text != null && text !== '') {
      doc.setFontSize(size)
      doc.setFont('helvetica', bold ? 'bold' : 'normal')
      doc.setTextColor(...color)
      const pad = 1.5
      let tx = x + pad
      if (align === 'center') tx = x + w / 2
      else if (align === 'right') tx = x + w - pad
      const ty = valign === 'top' ? y + 3.5 : y + h / 2
      doc.text(String(text), tx, ty, { align, baseline: valign === 'top' ? 'top' : 'middle' })
      doc.setTextColor(0)
    }
  }

  // Extract deco stops from a plan as {depth, time, cumul} rows.
  // cumul is the absolute runtime (adds runtimeOffset for bailout profiles).
  extractStopRows(plan) {
    if (!plan || !plan.segments) return []
    const offset = plan.runtimeOffset || 0
    return plan.segments
      .filter(s => s.type === 'stop')
      .map(s => ({
        depth: s.depth,
        time: Math.ceil(s.time),
        cumul: Math.ceil(s.runtime + offset)
      }))
  }

  // Extract descent and bottom phases from a plan as {phase, depth, time, cumul}
  // rows so the condensed sheet lists the full dive before the deco stops.
  // Handles multilevel dives (several descent/bottom segments, in order).
  // cumul is the absolute runtime (adds runtimeOffset for bailout profiles).
  extractDiveRows(plan) {
    if (!plan || !plan.segments) return []
    const offset = plan.runtimeOffset || 0
    return plan.segments
      .filter(s => s.type === 'descent' || s.type === 'bottom')
      .map(s => ({
        phase: s.type,
        depth: s.type === 'descent' ? s.endDepth : s.depth,
        time: Math.ceil(s.time),
        cumul: Math.ceil(s.runtime + offset)
      }))
  }

  // Build a condensed single-page A4 planning sheet pre-filled with the
  // computed plan (paper-form style: team block, bailout grid, run-time table).
  // One page fiche. `existingDoc` lets a caller chain several of them into one
  // document - a day of diving is one page per dive - and the caller owns the
  // page break: this method draws on the current page, wherever it is.
  generateCondensedPdf(existingDoc = null) {
    const { ccr: ccrPlan, bailout } = this.plan
    const isCCR = this.ccr.enabled
    const primaryPlan = isCCR ? ccrPlan : bailout
    const date = new Date().toLocaleDateString()
    const time = new Date().toLocaleTimeString()

    // Metadata only when this sheet owns the document: a caller chaining several
    // sheets (a day of diving) describes the whole file itself, and jsPDF keeps
    // the first XMP packet it is given.
    const doc = existingDoc || this.stampMetadata(createPdfDocument())
    const pageWidth = doc.internal.pageSize.getWidth()
    const margin = 15
    const contentW = pageWidth - 2 * margin
    let y = 18

    // Title
    doc.setFontSize(18)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(0)
    doc.text(this.t('pdf_form_title'), pageWidth / 2, y, { align: 'center' })

    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(120)
    doc.text(`${this.t('pdf_generated')}: ${date} ${time}`, pageWidth - margin, 12, { align: 'right' })
    doc.setTextColor(0)
    y += 6

    // --- Date / Site / Team row ---
    const rowH = 8
    const c1 = contentW * 0.33
    const c2 = contentW * 0.40
    const c3 = contentW - c1 - c2
    this.drawCell(doc, margin, y, c1, rowH, `${this.t('pdf_form_date')}: ${date}`, { bold: true, size: 9 })
    this.drawCell(doc, margin + c1, y, c2, rowH, `${this.t('settings_dive_site')}: ${this.settings.diveSite?.trim() || ''}`, { bold: true, size: 9 })
    this.drawCell(doc, margin + c1 + c2, y, c3, rowH, `${this.t('pdf_form_team')}:`, { bold: true, size: 9 })
    y += rowH

    // --- Divers block (Diver 1/2/3) ---
    const labelW = 28
    const diverW = (contentW - labelW) / 3
    const dRowH = 7

    // Header: Diver 1/2/3
    this.drawCell(doc, margin, y, labelW, dRowH, '', { fill: [255, 255, 255] })
    for (let i = 0; i < 3; i++) {
      this.drawCell(doc, margin + labelW + i * diverW, y, diverW, dRowH, `${this.t('pdf_form_diver')} ${i + 1}`, { bold: true, align: 'center', size: 10 })
    }
    y += dRowH

    // Pre-fill values for diver 1
    const diluentName = isCCR
      ? (this.ccr.diluent?.name || 'Air')
      : (this.getBottomGasName(bailout) || '')
    const gfText = `${Math.round(this.settings.gfLow * 100)}/${Math.round(this.settings.gfHigh * 100)}`
    const diverRows = [
      { label: this.t('pdf_form_lastname'), val1: this.settings.diverNames?.trim() || '' },
      { label: this.t('pdf_form_machine'), val1: '' },
      { label: this.t('plan_diluent'), val1: diluentName },
      { label: this.t('pdf_form_gf'), val1: gfText }
    ]
    diverRows.forEach(r => {
      this.drawCell(doc, margin, y, labelW, dRowH, r.label, { size: 8, bold: false })
      this.drawCell(doc, margin + labelW, y, diverW, dRowH, r.val1, { size: 9, align: 'center' })
      this.drawCell(doc, margin + labelW + diverW, y, diverW, dRowH, '', {})
      this.drawCell(doc, margin + labelW + 2 * diverW, y, diverW, dRowH, '', {})
      y += dRowH
    })

    // --- Bailout grid: %/Vol/Prs/Lit per diver, 4 bailout rows ---
    const subW = diverW / 4
    // Sub-header row
    this.drawCell(doc, margin, y, labelW, dRowH, '', {})
    const subLabels = [this.t('pdf_form_col_pct'), this.t('pdf_form_col_vol'), this.t('pdf_form_col_press'), this.t('pdf_form_col_liters')]
    for (let d = 0; d < 3; d++) {
      for (let s = 0; s < 4; s++) {
        this.drawCell(doc, margin + labelW + d * diverW + s * subW, y, subW, dRowH, subLabels[s], { size: 7, align: 'center', bold: true, fill: [245, 245, 245] })
      }
    }
    y += dRowH

    // Bailout gases to pre-fill in diver 1 column: gas code / Vol / Prs / Liters.
    // Divers 2 and 3 stay blank (the app models a single diver configuration).
    const formBailoutGases = this.getFormBailoutGases()
    // What the plan does to each of those cylinders. The grid says what is
    // carried; without this it never said whether that covers the bailout.
    const verdicts = this.gasVerdicts(bailout)
    let verdictShown = false
    for (let b = 0; b < 4; b++) {
      this.drawCell(doc, margin, y, labelW, dRowH, `Bailout ${b + 1}`, { size: 8 })
      const g = formBailoutGases[b]
      let diver1Vals = ['', '', '', '']
      let litersStyle = null
      if (g) {
        const vol = g.tankVolume
        const prs = g.tankPressure
        const liters = (vol != null && prs != null) ? vol * prs : null
        diver1Vals = [
          this.gasCodeOf(g.name),
          vol != null ? String(vol) : '',
          prs != null ? String(prs) : '',
          liters != null ? String(liters) : ''
        ]
        // Only a cylinder the diver described gets a verdict here. Without a
        // volume and a pressure the cell is blank, and the requirement is
        // measured against a default 12 L / 200 bar nobody carries: colouring
        // that would be a claim about gas that does not exist.
        litersStyle = liters != null ? this.gasVerdictColors(verdicts.get(g.name)) : null
        if (litersStyle) verdictShown = true
      }
      for (let d = 0; d < 3; d++) {
        for (let s = 0; s < 4; s++) {
          const val = d === 0 ? diver1Vals[s] : ''
          // The verdict marks the capacity cell, and only in the diver 1
          // column: the two others are the blank form of the other divers,
          // whose configuration the app does not hold.
          const style = (d === 0 && s === 3) ? litersStyle : null
          this.drawCell(doc, margin + labelW + d * diverW + s * subW, y, subW, dRowH, val, {
            size: 7,
            align: 'center',
            bold: Boolean(style),
            fill: style?.fill || null,
            color: style?.color || [0, 0, 0]
          })
        }
      }
      y += dRowH
    }

    // Legend of the colours above. A shade alone says nothing on a sheet that
    // gets photocopied, and the reader has to know which cell carries it.
    if (verdictShown) {
      y += 3.5
      doc.setFontSize(6.5)
      doc.setFont('helvetica', 'italic')
      doc.setTextColor(80)
      doc.splitTextToSize(this.t('pdf_form_gas_verdict_legend'), contentW).forEach(line => {
        doc.text(line, margin, y)
        y += 3
      })
      doc.setTextColor(0)
      doc.setFont('helvetica', 'normal')
      y += 0.5
    }

    y += 4

    // --- Run Time section ---
    doc.setFontSize(15)
    doc.setFont('helvetica', 'bold')
    doc.text(this.t('pdf_form_runtime_title'), pageWidth / 2, y + 3, { align: 'center' })
    y += 7

    const maxDepth = getMaxDepth(primaryPlan)
    const bottomTime = getBottomTime(primaryPlan)
    const tts = Math.ceil(primaryPlan?.tts || 0)
    const col3 = contentW / 3
    this.drawCell(doc, margin, y, col3, rowH, `${this.t('pdf_form_planned_depth')}: ${maxDepth} m`, { bold: true, size: 9 })
    this.drawCell(doc, margin + col3, y, col3, rowH, `${this.t('pdf_form_planned_bottom')}: ${bottomTime} min`, { bold: true, size: 9 })
    this.drawCell(doc, margin + 2 * col3, y, contentW - 2 * col3, rowH, `${this.t('pdf_form_dtr')}: ${tts} min`, { bold: true, size: 9 })
    y += rowH

    // Environment row, in the same style as the row above: this sheet is the
    // one printed and taken to the dive site, so it has to say in which water
    // and at which altitude the run times below are valid. One row whatever the
    // case, so the single page A4 layout is preserved: the altitude shares the
    // row when the dive is not at sea level, and the water takes the full width
    // otherwise.
    const envRows = this.environmentRows()
    const envCellW = contentW / envRows.length
    envRows.forEach(([label, value], i) => {
      const w = i === envRows.length - 1 ? contentW - i * envCellW : envCellW
      this.drawCell(doc, margin + i * envCellW, y, w, rowH, `${label}: ${value}`, { bold: true, size: 9 })
    })
    y += rowH

    // --- Paliers table ---
    y = this.renderCondensedStopsTable(doc, margin, y, contentW, isCCR, ccrPlan, bailout)

    // Footer
    const fy = doc.internal.pageSize.getHeight() - 14
    doc.setFontSize(8)
    doc.setTextColor(150)
    doc.text(`${this.t('pdf_footer_generated')} v${APP_VERSION}`, pageWidth / 2, fy, { align: 'center' })
    doc.setFontSize(7)
    doc.text(this.t('pdf_footer_verify'), pageWidth / 2, fy + 4, { align: 'center' })
    doc.setTextColor(0)

    return doc
  }

  // Verdict of the gas budget for every carried cylinder, keyed by gas name, in
  // the very words the requirement table uses. Single source with the plan
  // screen, the detailed sheet and the HTML export: the condensed sheet used to
  // build its own line and carried the verdict in colour alone, which a black
  // and white print or a colour blind reader loses entirely.
  gasVerdicts(bailout) {
    const rows = gasConsumptionRows(bailout?.gasConsumption || [], this.t.bind(this), { shorten: false })
    return new Map(rows.map(r => [r.name, r]))
  }

  // Fill and text colour of a verdict. Four states, never two of them drawn
  // alike: a cylinder emptied to the litre and one with 600 L of reserve both
  // printed plain black on the grid.
  gasVerdictColors(row) {
    if (!row) return null
    if (row.insufficient) return { fill: [250, 219, 219], color: [170, 0, 0] }
    if (row.tight) return { fill: [253, 235, 208], color: [170, 95, 0] }
    if (row.unused) return { fill: [235, 235, 235], color: [110, 110, 110] }
    return { fill: [223, 242, 223], color: [0, 110, 0] }
  }

  // Deepest bottom segment gas name (used as diluent/bottom gas hint in OC).
  getBottomGasName(plan) {
    if (!plan || !plan.segments) return ''
    const bottoms = plan.segments.filter(s => s.type === 'bottom')
    if (!bottoms.length) return ''
    const deepest = bottoms.reduce((a, b) => (b.depth > a.depth ? b : a))
    // Same reason as getInitialGasName: the whole name, in a 50 mm cell that
    // holds it, so the bottom gas is not the one line of the sheet abbreviating
    // into ambiguity.
    return this.gasLabelOf(deepest.gas) || ''
  }

  // Gases to list in the bailout grid, each with tank volume/pressure so the
  // form can show gas code + Vol + Prs + Liters. Prefer the explicit bailout
  // gases (CCR); otherwise derive from the plan's gas consumption (OC), which
  // carries tankVolume/tankPressure for every gas actually used.
  // In the order the plan breathes them, like every other gas list of the
  // sheet. The configured order was printed as is, so 'Bailout 2' was the
  // oxygen while the requirement column beside it, read in diving order, had
  // the oxygen third: two lists of the same four cylinders disagreeing on one
  // page. A cylinder the plan never opens keeps its configured rank at the end
  // of the grid - it is carried, it is simply never reached.
  getFormBailoutGases() {
    if (this.bailoutGases && this.bailoutGases.length > 0) {
      const order = new Map((this.plan.bailout?.gasConsumption || [])
        .map((c, i) => [c.name, i]))
      return this.bailoutGases
        .map((g, i) => ({
          name: g.name,
          tankVolume: g.tankVolume,
          tankPressure: g.tankPressure,
          rank: order.has(g.name) ? order.get(g.name) : Number.MAX_SAFE_INTEGER,
          declared: i
        }))
        .sort((a, b) => (a.rank - b.rank) || (a.declared - b.declared))
        .map(({ name, tankVolume, tankPressure }) => ({ name, tankVolume, tankPressure }))
    }
    const consumption = this.plan.bailout?.gasConsumption || []
    return consumption
      .filter(c => c.liters > 0)
      .map(c => ({
        name: c.name,
        tankVolume: c.tankVolume,
        tankPressure: c.tankPressure
      }))
  }

  // Render the run-time stops table. CCR mode splits CCR/Bailout columns;
  // OC mode shows a single duration/cumul pair. Gas requirement is drawn as a
  // merged right column spanning all data rows.
  // Gas switches of the bailout/OC plan, keyed by depth, so the condensed
  // table can flag them with a '*' and name the gas being switched to.
  // A switch may happen away from a deco stop (MOD switch during a free
  // ascent), hence the runtime: such a depth gets its own row in the table.
  getGasSwitches(plan) {
    const switches = new Map()
    if (!plan || !plan.segments) return switches
    const offset = plan.runtimeOffset || 0
    plan.segments
      .filter(s => s.type === 'gasChange')
      .forEach(s => {
        switches.set(s.depth, {
          gasName: this.gasLabelOf(s.gasName || s.gas || ''),
          cumul: Math.ceil(s.runtime + offset)
        })
      })
    return switches
  }

  // Name of the gas breathed at the start of the bailout/OC plan, shown on the
  // bottom row of the condensed table.
  getInitialGasName(plan) {
    if (!plan || !plan.segments) return ''
    const first = plan.segments.find(s => s.gas || s.gasName)
    if (!first) {
      console.error('getInitialGasName: no segment carries a gas name, bottom gas left blank')
      return ''
    }
    // The whole name: the bottom row named the gas on its own, so 'TX 17/37'
    // printed as 'TX' two lines above a '*TX14/48' switch, and the sheet no
    // longer said which cylinder the bailout starts on. The column is 56 mm
    // (CCR) or 70 mm (OC), so it holds.
    return this.gasLabelOf(first.gasName || first.gas)
  }

  renderCondensedStopsTable(doc, margin, y, contentW, isCCR, ccrPlan, bailout) {
    const ccrStops = isCCR ? this.extractStopRows(ccrPlan) : []
    const bailStops = this.extractStopRows(bailout)
    const gasSwitches = this.getGasSwitches(bailout)
    const bailoutInitialGas = this.getInitialGasName(bailout)

    // Merge stop depths and gas-switch depths (descending: deepest first).
    // A switch without a deco stop still deserves its own row.
    const depths = new Set()
    ccrStops.forEach(s => depths.add(s.depth))
    bailStops.forEach(s => depths.add(s.depth))
    gasSwitches.forEach((_, depth) => depths.add(depth))
    const sortedDepths = Array.from(depths).sort((a, b) => b - a)

    const ccrByDepth = new Map(ccrStops.map(s => [s.depth, s]))
    const bailByDepth = new Map(bailStops.map(s => [s.depth, s]))

    // Column widths
    let cols
    if (isCCR) {
      const gasW = 56
      const rest = contentW - gasW // 124
      const paW = 24
      const durCol = (rest - paW) / 4 // 25
      cols = { pa: paW, ccrDur: durCol, ccrCum: durCol, bDur: durCol, bCum: durCol, gas: gasW }
    } else {
      const gasW = 70
      const rest = contentW - gasW
      const paW = rest * 0.34
      const durCol = (rest - paW) / 2
      cols = { pa: paW, dur: durCol, cum: durCol, gas: gasW }
    }

    const hH = 6 // header cell height (one level)
    const headerH = isCCR ? hH * 2 : hH

    // --- Header ---
    let x = margin
    if (isCCR) {
      // Prof palier (tall)
      this.drawCell(doc, x, y, cols.pa, headerH, this.t('pdf_form_palier'), { bold: true, align: 'center', size: 8, fill: [240, 240, 240] })
      // CCR group
      this.drawCell(doc, x + cols.pa, y, cols.ccrDur + cols.ccrCum, hH, 'CCR', { bold: true, align: 'center', size: 9, fill: [240, 240, 240] })
      // Bailout group
      const bailLabel = `Bailout - ${this.t('pdf_form_mutualized')}: ${this.t('pdf_form_yes')} / ${this.t('pdf_form_no')}`
      this.drawCell(doc, x + cols.pa + cols.ccrDur + cols.ccrCum, y, cols.bDur + cols.bCum, hH, bailLabel, { bold: true, align: 'center', size: 7, fill: [240, 240, 240] })
      // Gas (tall)
      this.drawCell(doc, x + cols.pa + cols.ccrDur + cols.ccrCum + cols.bDur + cols.bCum, y, cols.gas, headerH, this.t('pdf_form_vol_liters'), { bold: true, align: 'center', size: 8, fill: [240, 240, 240] })
      // Sub-headers
      const sy = y + hH
      let sx = x + cols.pa
      this.drawCell(doc, sx, sy, cols.ccrDur, hH, this.t('pdf_form_duration'), { align: 'center', size: 8, fill: [248, 248, 248] })
      sx += cols.ccrDur
      this.drawCell(doc, sx, sy, cols.ccrCum, hH, this.t('pdf_form_cumul'), { align: 'center', size: 8, fill: [248, 248, 248] })
      sx += cols.ccrCum
      this.drawCell(doc, sx, sy, cols.bDur, hH, this.t('pdf_form_duration'), { align: 'center', size: 8, fill: [248, 248, 248] })
      sx += cols.bDur
      this.drawCell(doc, sx, sy, cols.bCum, hH, this.t('pdf_form_cumul'), { align: 'center', size: 8, fill: [248, 248, 248] })
    } else {
      this.drawCell(doc, x, y, cols.pa, headerH, this.t('pdf_form_palier'), { bold: true, align: 'center', size: 8, fill: [240, 240, 240] })
      this.drawCell(doc, x + cols.pa, y, cols.dur, headerH, this.t('pdf_form_duration'), { bold: true, align: 'center', size: 8, fill: [240, 240, 240] })
      this.drawCell(doc, x + cols.pa + cols.dur, y, cols.cum, headerH, this.t('pdf_form_cumul'), { bold: true, align: 'center', size: 8, fill: [240, 240, 240] })
      this.drawCell(doc, x + cols.pa + cols.dur + cols.cum, y, cols.gas, headerH, this.t('pdf_form_vol_liters'), { bold: true, align: 'center', size: 8, fill: [240, 240, 240] })
    }
    y += headerH

    // --- Data rows ---
    // Dive phases (descent + bottom) come first so the DP can follow the whole
    // plan, then the deco stops. The dive is flown on the loop (CCR) or on the
    // single OC gas, so its duration/cumul go in the CCR/main columns; in CCR
    // mode the bailout columns stay blank (bailout only starts at the switch).
    const diveRows = this.extractDiveRows(isCCR ? ccrPlan : bailout)
    const renderRows = [
      ...diveRows.map(d => ({ kind: 'dive', dive: d })),
      ...sortedDepths.map(depth => ({ kind: 'stop', depth }))
    ]

    const pageBottom = doc.internal.pageSize.getHeight() - 20
    const minRows = 12
    const dataCount = Math.max(renderRows.length, minRows)
    let rH = 5.5
    if (y + dataCount * rH > pageBottom) {
      rH = Math.max(4, (pageBottom - y) / dataCount)
    }

    const gasX = isCCR
      ? margin + cols.pa + cols.ccrDur + cols.ccrCum + cols.bDur + cols.bCum
      : margin + cols.pa + cols.dur + cols.cum
    const gasW = cols.gas
    const dataTop = y
    const diveFill = [225, 238, 252] // light blue to set dive phases apart from stops

    for (let i = 0; i < dataCount; i++) {
      const rr = renderRows[i]
      x = margin

      // Column values for this row (dive phase, deco stop, or empty padding)
      let paText = ''
      let rowFill = null
      let ccrDur = '', ccrCum = '', bDur = '', bCum = '', dur = '', cum = ''
      if (rr?.kind === 'dive') {
        const label = rr.dive.phase === 'descent' ? this.t('pdf_form_descent') : this.t('pdf_form_bottom')
        paText = `${label} ${rr.dive.depth}m`
        rowFill = diveFill
        if (isCCR) {
          ccrDur = `${rr.dive.time}'`
          ccrCum = `${rr.dive.cumul}'`
          // Bailout columns stay blank until the switch, except on the bottom
          // row where the initial bailout gas is named for the dive plan.
          if (rr.dive.phase === 'bottom' && bailoutInitialGas) bDur = bailoutInitialGas
        } else {
          dur = `${rr.dive.time}'`
          cum = `${rr.dive.cumul}'`
        }
      } else if (rr?.kind === 'stop') {
        paText = `${rr.depth} m`
        const sw = gasSwitches.get(rr.depth)
        const mark = sw ? ` *${sw.gasName}` : ''
        if (isCCR) {
          const cs = ccrByDepth.get(rr.depth)
          const bs = bailByDepth.get(rr.depth)
          ccrDur = cs ? `${cs.time}'` : ''
          ccrCum = cs ? `${cs.cumul}'` : ''
          bDur = bs ? `${bs.time}'${mark}` : mark.trim()
          bCum = bs ? `${bs.cumul}'` : (sw ? `${sw.cumul}'` : '')
        } else {
          const bs = bailByDepth.get(rr.depth)
          dur = bs ? `${bs.time}'${mark}` : mark.trim()
          cum = bs ? `${bs.cumul}'` : (sw ? `${sw.cumul}'` : '')
        }
      }

      // Cells carrying a gas name are longer than a plain duration: shrink the
      // font so they stay inside the column.
      const bDurSize = bDur.length > 6 ? 7 : 8
      const durSize = dur.length > 6 ? 7 : 8

      if (isCCR) {
        this.drawCell(doc, x, y, cols.pa, rH, paText, { align: 'center', size: 8, fill: rowFill })
        x += cols.pa
        this.drawCell(doc, x, y, cols.ccrDur, rH, ccrDur, { align: 'center', size: 8, fill: rowFill })
        x += cols.ccrDur
        this.drawCell(doc, x, y, cols.ccrCum, rH, ccrCum, { align: 'center', size: 8, fill: rowFill })
        x += cols.ccrCum
        this.drawCell(doc, x, y, cols.bDur, rH, bDur, { align: 'center', size: bDurSize, fill: rowFill })
        x += cols.bDur
        this.drawCell(doc, x, y, cols.bCum, rH, bCum, { align: 'center', size: 8, fill: rowFill })
      } else {
        this.drawCell(doc, x, y, cols.pa, rH, paText, { align: 'center', size: 8, fill: rowFill })
        x += cols.pa
        this.drawCell(doc, x, y, cols.dur, rH, dur, { align: 'center', size: durSize, fill: rowFill })
        x += cols.dur
        this.drawCell(doc, x, y, cols.cum, rH, cum, { align: 'center', size: 8, fill: rowFill })
      }
      y += rH
    }

    // Merged gas requirement column spanning all data rows
    const gasH = y - dataTop
    doc.setDrawColor(0)
    doc.setLineWidth(0.2)
    doc.rect(gasX, dataTop, gasW, gasH)
    this.drawGasRequirementText(doc, gasX, dataTop, gasW, gasH, bailout)

    // Legend for the gas-switch marker, only when at least one switch occurs.
    if (gasSwitches.size > 0) {
      y += 3.5
      doc.setFontSize(7)
      doc.setFont('helvetica', 'italic')
      doc.setTextColor(80)
      doc.text(`* = ${this.t('pdf_form_gas_switch_legend')}`, margin, y)
      doc.setTextColor(0)
      doc.setFont('helvetica', 'normal')
    }

    return y
  }

  // Draw the gas requirement inside the merged right column of the condensed
  // stops table: what each cylinder owes the plan, then whether it covers it.
  //
  // Every carried cylinder is listed, the ones the plan never opens included.
  // The column used to filter on `liters > 0`, so a cylinder the engine ignored
  // vanished from it while the bailout grid above kept announcing it.
  //
  // The verdict is spelled out. Colour used to carry it alone: on the 70 m
  // sheet, a 7 L emptied to 5 L of margin and a 12 L with 600 L of reserve
  // printed the same line, one orange and one black - the same line once
  // photocopied.
  drawGasRequirementText(doc, x, y, w, h, bailout) {
    const rows = this.gasVerdicts(bailout)
    const list = [...rows.values()]
    const pad = 2
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(0)
    if (list.length === 0) {
      doc.setFontSize(8)
      doc.setTextColor(120)
      doc.text('-', x + pad, y + 5)
      doc.setTextColor(0)
      return
    }

    // The verdict is the line that must survive the column height: the detail
    // figures, then the font size, give way before it does.
    const layouts = [
      { size: 8, leading: 4.5, detail: true },
      { size: 7.5, leading: 4.1, detail: true },
      { size: 7, leading: 3.8, detail: true },
      { size: 7, leading: 3.8, detail: false },
      { size: 6.5, leading: 3.3, detail: false }
    ]
    const blocksFor = layout => {
      doc.setFontSize(layout.size)
      return list.map(r => {
        // A cylinder the plan never opens owes nothing, so it carries its
        // capacity instead of a '0 L (0 bar)' requirement.
        const head = r.unused ? r.label : `${r.label}: ${r.neededText}`
        const verdict = layout.detail && r.detailText
          ? `${r.statusText} - ${r.detailText}`
          : r.statusText
        return {
          row: r,
          head: doc.splitTextToSize(head, w - 2 * pad),
          verdict: doc.splitTextToSize(verdict, w - 2 * pad)
        }
      })
    }
    const lineCount = blocks => blocks.reduce((n, b) => n + b.head.length + b.verdict.length, 0)
    const available = h - 4
    let layout = null
    let blocks = null
    for (const candidate of layouts) {
      const built = blocksFor(candidate)
      if (lineCount(built) * candidate.leading <= available) {
        layout = candidate
        blocks = built
        break
      }
    }
    if (!blocks) {
      layout = layouts[layouts.length - 1]
      blocks = blocksFor(layout)
      console.error('drawGasRequirementText: %d lines for %d cylinders do not fit in %dmm, the requirement column overflows',
        lineCount(blocks), list.length, Math.round(h))
    }

    doc.setFontSize(layout.size)
    let ty = y + 4
    blocks.forEach(({ row, head, verdict }) => {
      const colors = this.gasVerdictColors(row)
      head.forEach(line => {
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(0)
        doc.text(line, x + pad, ty)
        ty += layout.leading
      })
      verdict.forEach(line => {
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(...colors.color)
        doc.text(line, x + pad, ty)
        ty += layout.leading
      })
    })
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(0)
  }

  // Render a plan section (CCR or bailout/OC) as a PDF table with columns
  // for index, type, depth, time, runtime, gas, ppO2, END, and tissue info.
  // Includes CCR phase row (if bailout), deco stops summary, and safety alerts.
  renderPlanSectionPdf(doc, title, plan, isCCR, margin, y, pageWidth) {
    // Check if we need a new page
    if (y > 220) {
      doc.addPage()
      y = 20
    }

    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(0)
    doc.text(title, margin, y)
    y += 2
    doc.setDrawColor(200)
    doc.line(margin, y, pageWidth - margin, y)
    y += 6

    const segments = filterDisplaySegments(plan.segments)

    // Check for CCR phase (bailout simulation shows CCR phase before switch)
    const ccrPhase = plan.ccrPhase
    const runtimeOffset = plan.runtimeOffset || 0

    // Table header - add Type and Tissue columns.
    //
    // The density column is headed by its unit alone. 'Densité', 'Densidad' or
    // 'Gęstość' do not fit in the 11 mm the row leaves at font size 8, and g/l
    // reads the same in the six languages the document is translated into.
    //
    // Widths: the content area is 180 mm (A4 minus two 15 mm margins). The CCR
    // row adds up to 177 and the open circuit one to 178, so both keep a
    // millimetre of slack before the Tissue column runs into the right margin.
    const headers = isCCR
      ? ['#', this.t('plan_type'), this.t('plan_depth'), this.t('plan_time'), this.t('plan_runtime'), this.t('plan_gas'), 'SP', 'ppO2', 'END', 'g/l', this.t('plan_tissue')]
      : ['#', this.t('plan_type'), this.t('plan_depth'), this.t('plan_time'), this.t('plan_runtime'), this.t('plan_gas'), 'ppO2', 'END', 'g/l', this.t('plan_tissue')]
    const colWidths = isCCR
      ? [7, 22, 20, 14, 16, 26, 9, 12, 12, 11, 28]
      : [7, 20, 20, 16, 18, 30, 14, 14, 11, 28]
    // Index of the density column, used below to colour the cell
    const densityCol = isCCR ? 9 : 8

    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    doc.setFillColor(240, 240, 240)
    doc.rect(margin, y - 3, pageWidth - 2 * margin, 6, 'F')

    let x = margin
    headers.forEach((h, i) => {
      doc.text(h, x + 1, y)
      x += colWidths[i]
    })
    y += 5

    // Table rows
    doc.setFont('helvetica', 'normal')
    let rowIndex = 0

    // CCR phase row (for bailout simulation - shows CCR phase before switch)
    if (ccrPhase) {
      // Alternating row color (grey for CCR phase)
      doc.setFillColor(230, 230, 230)
      doc.rect(margin, y - 3, pageWidth - 2 * margin, 5, 'F')

      x = margin
      const displayRuntime = Math.ceil(ccrPhase.runtime)
      const ccrDensity = formatDensity(ccrPhase, this.environment)
      const ccrRow = isCCR
        ? ['-', 'CCR', `${ccrPhase.depth}m`, '-', `${displayRuntime} min`, `${ccrPhase.gas} SP${ccrPhase.setpoint}`, String(ccrPhase.setpoint), ccrPhase.ppO2?.toFixed(2) || '-', ccrPhase.end !== undefined && ccrPhase.end >= 0 ? `${ccrPhase.end}m` : '-', ccrDensity, '-']
        : ['-', 'CCR', `${ccrPhase.depth}m`, '-', `${displayRuntime} min`, `${ccrPhase.gas} SP${ccrPhase.setpoint}`, ccrPhase.ppO2?.toFixed(2) || '-', ccrPhase.end !== undefined && ccrPhase.end >= 0 ? `${ccrPhase.end}m` : '-', ccrDensity, '-']

      doc.setTextColor(100)
      ccrRow.forEach((cell, j) => {
        this.drawFittedText(doc, cell, x + 1, y, colWidths[j] - 2)
        x += colWidths[j]
      })
      doc.setTextColor(0)
      y += 5
      rowIndex++
    }

    segments.forEach((s, i) => {
      if (y > 270) {
        doc.addPage()
        y = 20
      }

      // Row background: alternate white/grey for readability
      if (rowIndex % 2 === 0) {
        doc.setFillColor(255, 255, 255) // White
      } else {
        doc.setFillColor(240, 240, 240) // Light grey
      }
      doc.rect(margin, y - 3, pageWidth - 2 * margin, 5, 'F')

      x = margin
      // Get depth display (for ascent/descent show start -> end)
      const depthDisplay = (s.type === 'ascent' || s.type === 'descent')
        ? `${s.startDepth}->${s.endDepth}m`
        : `${s.depth}m`

      // Get type label
      const typeLabel = getSegmentTypeLabel(s, this.t.bind(this))

      // For bailout, show total runtime (CCR + bailout)
      const displayRuntime = runtimeOffset > 0 ? Math.ceil(s.runtime + runtimeOffset) : Math.ceil(s.runtime)

      // Tissue info display (compact format: C5 85% N2)
      const tissueDisplay = s.tissueInfo
        ? `C${s.tissueInfo.compartment} ${s.tissueInfo.gfPercent}% ${s.tissueInfo.leadingGas}`
        : '-'

      const density = formatDensity(s, this.environment)
      const row = isCCR
        ? [String(i + 1), typeLabel, depthDisplay, `${Math.ceil(s.time)} min`, `${displayRuntime} min`, this.gasLabelOf(s.gas) || '-', s.setpoint != null ? String(s.setpoint) : '-', s.ppO2?.toFixed(2) || '-', s.end !== undefined && s.end >= 0 ? `${s.end}m` : '-', density, tissueDisplay]
        : [String(i + 1), typeLabel, depthDisplay, `${Math.ceil(s.time)} min`, `${displayRuntime} min`, this.gasLabelOf(s.gas) || '-', s.ppO2?.toFixed(2) || '-', s.end !== undefined && s.end >= 0 ? `${s.end}m` : '-', density, tissueDisplay]

      // Same thresholds as the density alerts printed under the table, read
      // from the same helper: a plain cell under a critical alert would be a
      // contradiction inside one page.
      const densityStyle = densityLevel(segmentDensity(s, this.environment), isCCR)

      row.forEach((cell, j) => {
        // Highlight high ppO2
        if ((isCCR && j === 7) || (!isCCR && j === 6)) {
          if (s.ppO2 && s.ppO2 > 1.4) {
            doc.setTextColor(133, 100, 4)
          }
        }
        if (j === densityCol) {
          if (densityStyle === 'critical') doc.setTextColor(163, 38, 28)
          else if (densityStyle === 'high') doc.setTextColor(133, 100, 4)
        }
        this.drawFittedText(doc, cell, x + 1, y, colWidths[j] - 2)
        doc.setTextColor(0)
        x += colWidths[j]
      })
      y += 5
      rowIndex++
    })

    y += 3

    // Deco stops summary
    doc.setFontSize(9)
    if (plan.stops?.length > 0) {
      doc.setFont('helvetica', 'bold')
      const stopsLabel = `${this.t('plan_stops_summary')}: `
      doc.text(stopsLabel, margin, y)
      const stopsLabelWidth = doc.getTextWidth(stopsLabel)
      doc.setFont('helvetica', 'normal')
      const stopsText = plan.stops.map(s => `${s.depth}m: ${s.time}min`).join(' | ')
      const maxStopsWidth = pageWidth - margin - stopsLabelWidth - margin
      const stopsLines = doc.splitTextToSize(stopsText, maxStopsWidth)
      stopsLines.forEach((line, i) => {
        doc.text(line, margin + stopsLabelWidth, y + i * 4)
      })
      y += stopsLines.length * 4
    } else {
      doc.text(this.t('plan_no_deco'), margin, y)
    }
    y += 6

    // Analyze and render alerts (PDF shows all alerts regardless of user settings toggles).
    // planWarnings carries the engine warnings of this very plan (truncated stop,
    // runtime limit, gas fallback): a printed plan taken underwater has to say
    // that it is incomplete.
    const { alerts } = analyzeAlerts(segments, {
      ccrPhase,
      isCCR,
      environment: this.environment,
      translateFn: (key, params) => this.t(key, params),
      planWarnings: plan.warnings || []
    })
    y = this.renderAlertsPdf(doc, alerts, margin, y, pageWidth)

    return y
  }

  renderGasConsumptionPdf(doc, consumption, title, margin, y, pageWidth, isBailout = true) {
    // Cylinders at 0 L are printed too: a carried cylinder the plan never
    // opens has to appear next to one the plan declares short, or the sheet
    // announces four bailout bottles and accounts for three.
    const rows = gasConsumptionRows(consumption, this.t.bind(this), { shorten: false })
    if (rows.length === 0) return y

    if (y > 230) {
      doc.addPage()
      y = 20
    }

    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(0)
    doc.text(title, margin, y)
    y += 2
    doc.setDrawColor(200)
    doc.line(margin, y, pageWidth - margin, y)
    y += 6

    // Table header - same as screen: Gas, Tank, Needed, Status
    const headers = [this.t('plan_gas'), this.t('plan_tank'), this.t('plan_needed'), this.t('plan_status')]
    const colWidths = [35, 40, 45, 50]

    doc.setFontSize(8)
    doc.setFont('helvetica', 'bold')
    doc.setFillColor(240, 240, 240)
    doc.rect(margin, y - 3, pageWidth - 2 * margin, 6, 'F')

    let x = margin
    headers.forEach((h, i) => {
      doc.text(h, x + 1, y)
      x += colWidths[i]
    })
    y += 5

    // Table rows - same layout as screen
    doc.setFont('helvetica', 'normal')
    rows.forEach(r => {
      x = margin

      this.drawFittedText(doc, r.label, x + 1, y, colWidths[0] - 2)
      x += colWidths[0]

      doc.text(r.tankText, x + 1, y)
      x += colWidths[1]

      doc.text(r.neededText, x + 1, y)
      x += colWidths[2]

      // Status with colour: red when short, grey when the cylinder is carried
      // but never breathed, orange when the plan empties it to the litre, green
      // when it covers what the plan asks with something left over.
      if (r.insufficient) {
        doc.setTextColor(200, 0, 0)
      } else if (r.unused) {
        doc.setTextColor(110, 110, 110)
      } else if (r.tight) {
        doc.setTextColor(200, 120, 0)
      } else {
        doc.setTextColor(0, 150, 0)
      }
      doc.text(r.statusText, x + 1, y)
      doc.setTextColor(100)
      doc.setFontSize(7)
      doc.text(r.detailText, x + 1, y + 3.5)
      if (r.backupText) doc.text(r.backupText, x + 1, y + 6.5)
      doc.setFontSize(8)
      doc.setTextColor(0)

      y += r.backupText ? 11 : 8
    })

    // RMV info - detailed based on mode and stress settings
    y += 2
    doc.setFontSize(8)
    doc.setTextColor(100)

    let rmvInfo
    if (isBailout) {
      // For bailout: show stress RMV with stress until option, then deco RMV
      const stressUntilText = getStressUntilText(this.settings.stressUntil, this.t)
      rmvInfo = `${this.t('plan_based_on_rmv')}: ${this.settings.rmvStress} L/min (${stressUntilText}) -> ${this.settings.rmvDeco} L/min (deco)`
    } else {
      // For OC: show bottom/deco RMV
      rmvInfo = `${this.t('plan_based_on_rmv')}: ${this.settings.rmvBottom}/${this.settings.rmvDeco} L/min`
    }

    doc.text(rmvInfo, margin, y)
    doc.setTextColor(0)
    y += 10

    return y
  }

  // Render notes section in PDF
  renderNotesPdf(doc, notes, margin, y, pageWidth) {
    if (y > 240) {
      doc.addPage()
      y = 20
    }

    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(0)
    doc.text(this.t('plan_notes'), margin, y)
    y += 2
    doc.setDrawColor(200)
    doc.line(margin, y, pageWidth - margin, y)
    y += 6

    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')

    // Split notes into lines that fit the page width
    const maxWidth = pageWidth - 2 * margin
    const lines = doc.splitTextToSize(notes, maxWidth)

    lines.forEach(line => {
      if (y > 270) {
        doc.addPage()
        y = 20
      }
      doc.text(line, margin, y)
      y += 5
    })

    y += 5
    return y
  }

  // Render alerts section in PDF.
  //
  // Every message is wrapped to the box width and the box is split across pages
  // when needed. The ceiling violation and the engine warnings relayed from
  // result.warnings are full sentences roughly 190 to 260 mm wide at font size
  // 8, that is wider than the 174 mm of usable page: jsPDF happily draws them
  // past the MediaBox, where no viewer and no printer shows them. A safety
  // alert silently cropped off the sheet the diver carries is worse than no
  // alert at all, hence splitTextToSize plus the page break below.
  renderAlertsPdf(doc, alerts, margin, y, pageWidth) {
    if (!alerts || alerts.length === 0) return y

    const errorAlerts = alerts.filter(a => a.type === 'error')
    const warningAlerts = alerts.filter(a => a.type === 'warning')

    const boxWidth = pageWidth - 2 * margin
    const textWidth = boxWidth - 6 // 3 mm padding on each side
    const lineHeight = 4
    // Keep clear of the footer block drawn at pageHeight - 24
    const pageBottom = doc.internal.pageSize.getHeight() - 26

    // Section title, kept on the same page as the first alert box
    if (y + 10 > pageBottom) {
      doc.addPage()
      y = 20
    }
    doc.setFontSize(9)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(0)
    doc.text(this.t('alerts_summary_title'), margin, y)
    doc.setFont('helvetica', 'normal')
    y += 5

    doc.setFontSize(8)

    // Draw one colored box holding every message of `list`, wrapped to the box
    // width, starting at `startY`. Returns the y below the last box drawn.
    const drawAlertBox = (list, fill, border, textColor, startY) => {
      if (list.length === 0) return startY

      const lines = []
      list.forEach(alert => {
        doc.splitTextToSize(alert.message, textWidth).forEach(line => lines.push(line))
      })

      let cursor = startY
      let drawn = 0
      while (drawn < lines.length) {
        // Never start a box that cannot hold at least one line of text
        if (cursor + 4 + lineHeight > pageBottom) {
          doc.addPage()
          cursor = 20
        }
        const room = Math.floor((pageBottom - cursor - 4) / lineHeight)
        const chunk = lines.slice(drawn, drawn + room)
        const boxHeight = 4 + chunk.length * lineHeight

        doc.setFillColor(fill[0], fill[1], fill[2])
        doc.setDrawColor(border[0], border[1], border[2])
        doc.roundedRect(margin, cursor, boxWidth, boxHeight, 1, 1, 'FD')

        doc.setTextColor(textColor[0], textColor[1], textColor[2])
        chunk.forEach((line, i) => {
          doc.text(line, margin + 3, cursor + 3 + i * lineHeight)
        })

        cursor += boxHeight + 5
        drawn += chunk.length
      }
      return cursor
    }

    // Error alerts (red box), then warning alerts (yellow box)
    y = drawAlertBox(errorAlerts, [254, 226, 226], [220, 38, 38], [153, 27, 27], y)
    y = drawAlertBox(warningAlerts, [254, 249, 195], [202, 138, 4], [133, 77, 14], y)

    doc.setTextColor(0)
    // Add extra spacing after alerts to prevent overlap with next section
    y += 4
    return y
  }

  // Render a miniature dive profile chart in the PDF using jsPDF line drawing.
  // Draws depth grid, ceiling curve (dashed red), profile line (green or orange),
  // and optional CCR overlay (blue) when ccrPlan is provided for bailout display.
  // Supports Y-axis compression for deep dives with shallow deco stops.
  renderProfilePdf(doc, plan, margin, y, pageWidth, ccrPlan = null) {
    const profileData = getProfileData(plan)
    if (!profileData || profileData.length < 2) return y

    // For bailout with ccrPlan, we need to combine the two profiles
    // The bailout plan has runtimeOffset which is the CCR time before bailout
    const ccrProfileData = ccrPlan ? getProfileData(ccrPlan) : null
    const runtimeOffset = plan.runtimeOffset || 0

    // Check if we need a new page
    if (y > 200) {
      doc.addPage()
      y = 20
    }

    // Title - different for CCR vs Bailout
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(0)
    doc.text(this.t('profile_title'), margin, y)
    y += 2
    doc.setDrawColor(200)
    doc.line(margin, y, pageWidth - margin, y)
    y += 6

    // Profile dimensions
    const chartWidth = pageWidth - 2 * margin
    const chartHeight = 50
    const chartX = margin
    const chartY = y
    const padding = 8

    // For bailout profile, add runtimeOffset to all times
    let adjustedProfileData = profileData
    if (runtimeOffset > 0) {
      adjustedProfileData = profileData.map(p => ({
        ...p,
        time: p.time + runtimeOffset
      }))
    }

    // Calculate scales - consider both CCR and bailout data if present
    let allPoints = adjustedProfileData
    if (ccrProfileData) {
      allPoints = [...ccrProfileData, ...adjustedProfileData]
    }
    const maxDepth = Math.max(...allPoints.map(p => Math.max(p.depth, p.ceiling))) + 5
    const maxTime = Math.max(...allPoints.map(p => p.time)) + 5

    // Find first deco stop depth for Y-axis compression
    // Look for the first ascending segment that stays at same depth (deco stop)
    let firstStopDepth = null
    const mainPlan = ccrPlan || plan
    if (mainPlan && mainPlan.segments) {
      for (const seg of mainPlan.segments) {
        if (seg.type === 'STOP' || seg.type === 'stop') {
          firstStopDepth = seg.depth
          break
        }
      }
    }

    // Compression settings: only compress if max depth is significantly deeper than first stop
    // and first stop is at least 20m deep (meaningful deco)
    const useCompression = firstStopDepth && firstStopDepth >= 15 && (maxDepth - firstStopDepth) > 20
    const compressionCutoff = useCompression ? firstStopDepth + 5 : maxDepth // Add 5m margin below first stop
    const upperZoneRatio = 0.75 // Upper zone (0 to cutoff) gets 75% of chart height
    const lowerZoneRatio = 0.25 // Lower zone (cutoff to max) gets 25% of chart height

    // Draw background
    doc.setFillColor(240, 248, 255)
    doc.rect(chartX, chartY, chartWidth, chartHeight, 'F')

    // Helper to convert depth to Y coordinate with optional compression
    const toY = (depth) => {
      const innerHeight = chartHeight - 2 * padding
      const topY = chartY + padding

      if (!useCompression) {
        // Linear scale
        return topY + (depth / maxDepth) * innerHeight
      }

      // Compressed scale
      if (depth <= compressionCutoff) {
        // Upper zone: normal scale from 0 to cutoff
        return topY + (depth / compressionCutoff) * innerHeight * upperZoneRatio
      } else {
        // Lower zone: compressed scale from cutoff to maxDepth
        const upperZoneY = topY + innerHeight * upperZoneRatio
        const depthInLowerZone = depth - compressionCutoff
        const lowerZoneDepthRange = maxDepth - compressionCutoff
        return upperZoneY + (depthInLowerZone / lowerZoneDepthRange) * innerHeight * lowerZoneRatio
      }
    }

    // Draw grid
    doc.setDrawColor(200, 220, 240)
    doc.setLineWidth(0.1)

    // Depth grid lines - adapted for compression
    const depthStep = maxDepth > 60 ? 20 : (maxDepth > 30 ? 10 : 5)
    for (let d = 0; d <= maxDepth; d += depthStep) {
      const gy = toY(d)
      doc.line(chartX + padding, gy, chartX + chartWidth - padding, gy)
    }

    // Time grid lines
    const timeStep = maxTime > 120 ? 30 : (maxTime > 60 ? 15 : 10)
    for (let t = 0; t <= maxTime; t += timeStep) {
      const gx = chartX + padding + (t / maxTime) * (chartWidth - 2 * padding)
      doc.line(gx, chartY + padding, gx, chartY + chartHeight - padding)
    }

    // Helper to convert time to X coordinate
    const toX = (time) => chartX + padding + (time / maxTime) * (chartWidth - 2 * padding)

    // Draw ceiling curve (dashed red) for the main profile
    doc.setDrawColor(248, 114, 114)
    doc.setLineWidth(0.3)
    for (let i = 1; i < adjustedProfileData.length; i++) {
      const x1 = toX(adjustedProfileData[i - 1].time)
      const y1 = toY(adjustedProfileData[i - 1].ceiling)
      const x2 = toX(adjustedProfileData[i].time)
      const y2 = toY(adjustedProfileData[i].ceiling)
      // Dashed line simulation
      const segments = 5
      for (let s = 0; s < segments; s += 2) {
        const sx1 = x1 + (x2 - x1) * (s / segments)
        const sy1 = y1 + (y2 - y1) * (s / segments)
        const sx2 = x1 + (x2 - x1) * (Math.min(s + 1, segments) / segments)
        const sy2 = y1 + (y2 - y1) * (Math.min(s + 1, segments) / segments)
        doc.line(sx1, sy1, sx2, sy2)
      }
    }

    // Draw CCR profile first (blue, dotted) if we have CCR data for bailout profile
    if (ccrProfileData) {
      doc.setDrawColor(59, 130, 246) // Blue for CCR
      doc.setLineWidth(0.4)
      for (let i = 1; i < ccrProfileData.length; i++) {
        doc.line(
          toX(ccrProfileData[i - 1].time),
          toY(ccrProfileData[i - 1].depth),
          toX(ccrProfileData[i].time),
          toY(ccrProfileData[i].depth)
        )
      }
    }

    // Draw main profile line (green for CCR-only, orange for bailout)
    const profileColor = ccrProfileData ? [249, 115, 22] : [54, 211, 153] // Orange for bailout, green for CCR
    doc.setDrawColor(...profileColor)
    doc.setLineWidth(0.5)
    for (let i = 1; i < adjustedProfileData.length; i++) {
      doc.line(
        toX(adjustedProfileData[i - 1].time),
        toY(adjustedProfileData[i - 1].depth),
        toX(adjustedProfileData[i].time),
        toY(adjustedProfileData[i].depth)
      )
    }

    // Draw depth labels - use toY() for correct positioning with compression
    doc.setFontSize(6)
    doc.setTextColor(100)
    for (let d = 0; d <= maxDepth; d += depthStep) {
      const ly = toY(d)
      doc.text(`${d}m`, chartX + 1, ly + 1)
    }

    // Draw time labels
    for (let t = 0; t <= maxTime; t += timeStep) {
      const lx = chartX + padding + (t / maxTime) * (chartWidth - 2 * padding)
      doc.text(`${t}'`, lx - 2, chartY + chartHeight - 1)
    }

    // Legend
    doc.setFontSize(7)
    const legendY = chartY + chartHeight + 4
    let legendX = margin

    if (ccrProfileData) {
      // Bailout profile: show CCR (blue) and Bailout (orange)
      doc.setTextColor(59, 130, 246)
      doc.text('—', legendX, legendY)
      doc.setTextColor(80)
      doc.text('CCR', legendX + 4, legendY)
      legendX += 18

      doc.setTextColor(249, 115, 22)
      doc.text('—', legendX, legendY)
      doc.setTextColor(80)
      doc.text('Bailout', legendX + 4, legendY)
      legendX += 22
    } else {
      // CCR or OC profile: show profile (green)
      doc.setTextColor(54, 211, 153)
      doc.text('—', legendX, legendY)
      doc.setTextColor(80)
      doc.text(this.t('nav_profile'), legendX + 4, legendY)
      legendX += 25
    }

    doc.setTextColor(248, 114, 114)
    doc.text('- -', legendX, legendY)
    doc.setTextColor(80)
    doc.text(this.t('profile_ceiling'), legendX + 5, legendY)

    doc.setTextColor(0)
    y = chartY + chartHeight + 12

    return y
  }

  // Download PDF via <a download> link (works in most WebViews)
  downloadPdfFallback(doc, filename) {
    try {
      const blob = doc.output('blob')
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      setTimeout(() => {
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      }, 1000)
      debugPdfExport('downloadPdfFallback: triggered download via <a>')
    } catch (e2) {
      console.error('Fallback download also failed:', e2)
      alert('PDF export failed: ' + e2.message)
    }
  }

  // Native export: write to cache + share via system dialog
  async nativeExport(doc, filename, title, text) {
    let step = 'base64'
    try {
      const base64 = doc.output('datauristring').split(',')[1]

      step = 'writeFile'
      const result = await Filesystem.writeFile({
        path: filename,
        data: base64,
        directory: Directory.Cache,
      })

      step = 'share'
      await Share.share({
        title: title || filename,
        text: text || '',
        url: result.uri,
      })
      return true
    } catch (e) {
      if (e.message?.includes('canceled') || e.message?.includes('cancelled')) {
        return true
      }
      console.error(`Native export failed at step "${step}":`, e)
      return false
    }
  }

  // Build the requested document ('complete' full report or 'condensed' 1-page)
  buildDoc(format = 'complete') {
    return format === 'condensed' ? this.generateCondensedPdf() : this.generatePdf()
  }

  // Export to PDF using jsPDF
  async exportToPdf(format = 'complete') {
    let doc
    try {
      doc = this.buildDoc(format)
    } catch (e) {
      console.error('PDF generation failed:', e)
      return
    }

    const filename = `dive-plan-${new Date().toISOString().split('T')[0]}.pdf`
    const native = isNativePlatform()

    if (native) {
      const ok = await this.nativeExport(doc, filename)
      if (!ok) {
        this.downloadPdfFallback(doc, filename)
      }
    } else {
      doc.save(filename)
    }
  }

  // Share PDF via native share sheet (Capacitor) or Web Share API
  async shareNative(title, text, format = 'complete') {
    let doc
    try {
      doc = this.buildDoc(format)
    } catch (e) {
      console.error('PDF generation failed:', e)
      throw e
    }

    const filename = `dive-plan-${new Date().toISOString().split('T')[0]}.pdf`

    const ok = await this.nativeExport(doc, filename, title, text)
    if (!ok) {
      debugPdfExport('shareNative: native failed, trying <a download> fallback')
      this.downloadPdfFallback(doc, filename)
    }
  }

  // Get PDF as blob for sharing (web only)
  getPdfBlob(format = 'complete') {
    const doc = this.buildDoc(format)
    return doc.output('blob')
  }

  // Delegate HTML export to HtmlExport
  exportToHtml() {
    const htmlExporter = new HtmlExport(this.plan, this.settings, this.ccr, this.t, this.bailoutGases, this.environment)
    htmlExporter.exportToHtml()
  }
}

export default PdfExport
